import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, IconButton, Paper, Alert, CircularProgress,
} from '@mui/material';
import { Visibility as EyeIcon, Edit as EditIcon } from '@mui/icons-material';
import { Download as DownloadIcon } from '@mui/icons-material';
import {
  GET_SERVICE_ENDPOINT, GET_DASHBOARD_STATS_ENDPOINT,
  PUT_SERVICE_ENDPOINT, EXPORT_SERVICE_ENDPOINT,
  GET_SERVICE_CHOICES_ENDPOINT,
} from "../../config/urls";
import DoorLocksFeatureLayout from './DoorLocksFeatureLayout';
import { ServiceEditModal } from './EditModals';
import dayjs from 'dayjs';
import { formatWithTzLabel } from '../../utils/dateUtils';
import AppTable from '../../common/app-table';
import TableSearchInput from '../TableSearchInput';
import AppDropdown from '../../common/app-dropdown';
import AppDatePicker from '../../common/app-date-picker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { grayFontColor } from '../../cssUtils/constants';
import ButtonOne from '../../common/normal-button';
import ButtonTwo from '../../common/dashboard-button';

function getStatusColor(status) {
  const colors = {
    'New': 'primary', 'Open': 'primary', 'Scheduled': 'info', 'In Progress': 'warning',
    'Completed': 'success', 'Resolved': 'success', 'Urgent': 'error', 'Cancelled': 'error',
  };
  return colors[status] || 'default';
}

export default function ServiceTab({ activeTab = 1, counts = {}, onTabChange, onBack, onNavigate }) {
  const [servicesData, setServicesData]   = useState([]);
  const [totalCount, setTotalCount]       = useState(0);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [editError, setEditError]         = useState(null);
  const [editSuccess, setEditSuccess]     = useState(false);
  const [page, setPage]                   = useState(0);
  const [rowsPerPage, setRowsPerPage]     = useState(20);
  const [stats, setStats]                 = useState(null);
  const [loadingStats, setLoadingStats]   = useState(true);
  const [exporting, setExporting]         = useState(false);
  const [charCount, setCharCount]         = useState(0);

  // Filters
  const [searchQuery, setSearchQuery]         = useState('');
  const [cityFilter, setCityFilter]           = useState('');
  const [issueTypeFilter, setIssueTypeFilter] = useState('');
  const [lockTypeFilter, setLockTypeFilter]   = useState('');
  const [statusFilter, setStatusFilter]       = useState('');
  const [updatedByFilter, setUpdatedByFilter]     = useState('');
  const [updatedDateFilter, setUpdatedDateFilter] = useState('');

  // Filter dropdown choices (city / lock type / issue type / status / updated_by), fetched from service_choices
  const [filterChoices, setFilterChoices] = useState({ cities: [], lock_types: [], issue_types: [], service_statuses: [], updated_by: [] });

  // Modals
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalOpen, setModalOpen]           = useState(false);
  const [editModalOpen, setEditModalOpen]   = useState(false);

  const [tab, setTab] = useState(activeTab);
  const handleTabChange = onTabChange || ((e, newValue) => {
    setTab(newValue);
    if (onNavigate) {
      const tabKeyMap = ["installation", "service", "sales", "billing_dashboard"];
      newValue === 3 ? onNavigate("insights") : onNavigate(tabKeyMap[newValue] || "installation");
    }
  });
  const handleBack = onBack || (() => { onNavigate ? onNavigate("insights") : window.history.back(); });

  const [editFormData, setEditFormData] = useState({
    ticket_code: '', serial_number: '', customer_phone: '', wa_number: '', wa_name: '',
    customer_name: '', issue_type: '', lock_type: '', service_status: 'Scheduled',
    address: { flat: '', street: '', area: '', pincode: '', city: '', state: '', country: '' },
    issue_description: '', resolution_notes: '', requirements: '',
  });

  const getToken = () => localStorage.getItem('token');

  // ── Stats ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    setLoadingStats(true);
    fetch(GET_DASHBOARD_STATS_ENDPOINT, { headers: { 'X-Tenant-ID': 'dormakaba' }, method: 'GET' })
      .then(res => res.json()).then(setStats).catch(() => setStats(null))
      .finally(() => setLoadingStats(false));
  }, []);

  const effectiveCounts = stats ? {
    installation: stats.total_installations, service: stats.total_services,
    sales: stats.total_sales, tickets: stats.total_tickets, ...counts,
  } : { installation: '-', service: '-', sales: '-', tickets: '-', ...counts };

  // ── Filter choices (city / lock type / status) ──────────────────────────────

  // Re-run after every successful edit so new statuses / updated-by names show up in the filters
  const fetchFilterChoices = () => {
    const token = getToken();
    fetch(GET_SERVICE_CHOICES_ENDPOINT, {
      headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': localStorage.getItem('tenant') },
      method: 'GET',
    })
      .then(res => res.json())
      .then(data => setFilterChoices({
        cities: data.cities || [],
        lock_types: data.lock_types || [],
        issue_types: data.issue_types || [],
        service_statuses: data.service_statuses || [],
        updated_by: data.updated_by || [],
      }))
      .catch(() => setFilterChoices({ cities: [], lock_types: [], issue_types: [], service_statuses: [], updated_by: [] }));
  };

  useEffect(() => { fetchFilterChoices(); }, []);

  const cityOptions = React.useMemo(() => [
    { label: "All Cities", value: "" },
    ...filterChoices.cities.map((c) => ({ label: c.city_name, value: c.city_name })),
  ], [filterChoices.cities]);

  const lockTypeOptions = React.useMemo(() => [
    { label: "All Lock Types", value: "" },
    ...filterChoices.lock_types.map((l) => ({ label: l.lock_name, value: l.lock_name })),
  ], [filterChoices.lock_types]);

  const statusOptions = React.useMemo(() => [
    { label: "All Statuses", value: "" },
    ...filterChoices.service_statuses.map((s) => ({ label: s, value: s })),
  ], [filterChoices.service_statuses]);

  const issueTypeOptions = React.useMemo(() => [
    { label: "All Issue Types", value: "" },
    ...filterChoices.issue_types.map((t) => ({ label: t, value: t })),
  ], [filterChoices.issue_types]);

  const updatedByOptions = React.useMemo(() => [
    { label: "All Updated By", value: "" },
    ...filterChoices.updated_by.map((n) => ({ label: n, value: n })),
  ], [filterChoices.updated_by]);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchServicesData = async () => {
    setLoading(true); setError(null);
    const token = getToken();
    const params = new URLSearchParams();
    if (searchQuery)    params.append('search', searchQuery);
    if (cityFilter)     params.append('city', cityFilter);
    if (issueTypeFilter) params.append('issue_type', issueTypeFilter);
    if (lockTypeFilter) params.append('lock_type', lockTypeFilter);
    if (statusFilter)   params.append('status', statusFilter);
    if (updatedByFilter)   params.append('updated_by', updatedByFilter);
    if (updatedDateFilter) params.append('updated_date', updatedDateFilter);
    let endpoint = `${GET_SERVICE_ENDPOINT}?limit=${rowsPerPage}&offset=${page * rowsPerPage}`;
    if (params.toString()) endpoint += `&${params.toString()}`;

    try {
      const response = await fetch(endpoint, {
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json", "X-Tenant-Id": localStorage.getItem('tenant') },
      });
      if (!response.ok) throw new Error('Failed to fetch services data');
      const jsonData = await response.json();
      setServicesData(jsonData.results || []);
      setTotalCount(jsonData.count || 0);
    } catch (error) {
      console.error('Error fetching services data:', error.message);
      setError('Failed to load services. Please try again.');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchServicesData(); }, [page, rowsPerPage, searchQuery, cityFilter, issueTypeFilter, lockTypeFilter, statusFilter, updatedByFilter, updatedDateFilter]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleSearch = (e) => {
    const value = e.target.value;
    if (value.length > 50) return;
    setSearchQuery(value);
    setCharCount(value.length);
    setPage(0);
  };

  const handleChangePage        = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

  const handleEdit = (record) => {
    setEditFormData({
      service_id: record.service_id, ticket_code: record.ticket_code,
      serial_number: record.serial_number, customer_phone: record.customer_phone,
      wa_number: record.wa_number, customer_name: record.customer_name,
      wa_name: record.wa_name, issue_type: record.issue_type, lock_type: record.lock_type,
      service_status: record.service_status, address: { ...record.address_details },
      issue_description: record.issue_description || '',
      resolution_notes: record.resolution_notes || '',
      requirements: record.requirements || '',
    });
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    const token = getToken();
    try {
      const response = await fetch(`${PUT_SERVICE_ENDPOINT}${editFormData.service_id}/`, {
        method: 'PUT',
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json", "X-Tenant-Id": localStorage.getItem('tenant') },
        body: JSON.stringify(editFormData),
      });
      if (response.ok) { setEditError(null); setEditSuccess(true); fetchFilterChoices(); }
      else {
        const data = await response.json();
        const msgs = [];
        if (data?.ticket?.length)             msgs.push(`Ticket Error: ${data.ticket.join(' ')}`);
        if (data?.address?.length)            msgs.push(`Address Error: ${data.address.join(' ')}`);
        if (data?.customer_phone?.length)     msgs.push(`Phone Error: ${data.customer_phone.join(' ')}`);
        if (data?.customer_name?.length)      msgs.push(`Name Error: ${data.customer_name.join(' ')}`);
        if (data?.issue_type?.length)         msgs.push(`Issue Type Error: ${data.issue_type.join(' ')}`);
        if (data?.lock_type?.length)          msgs.push(`Lock Type Error: ${data.lock_type.join(' ')}`);
        if (data?.service_status?.length)     msgs.push(`Service Status Error: ${data.service_status.join(' ')}`);
        if (data?.issue_description?.length)  msgs.push(`Issue Description Error: ${data.issue_description.join(' ')}`);
        setEditError(msgs.length ? msgs.join(' | ') : 'Failed to update service. Please try again.');
      }
      fetchServicesData();
    } catch (err) {
      console.error('Error updating service:', err);
      setEditError('Failed to update service. Please try again.');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    const token = getToken();
    const params = new URLSearchParams();
    if (searchQuery)     params.append('search', searchQuery);
    if (cityFilter)      params.append('city', cityFilter);
    if (issueTypeFilter) params.append('issue_type', issueTypeFilter);
    if (lockTypeFilter)  params.append('lock_type', lockTypeFilter);
    if (statusFilter)    params.append('status', statusFilter);
    if (updatedByFilter)   params.append('updated_by', updatedByFilter);
    if (updatedDateFilter) params.append('updated_date', updatedDateFilter);
    let exportUrl = EXPORT_SERVICE_ENDPOINT;
    if (params.toString()) exportUrl += `?${params.toString()}`;
    try {
      const response = await fetch(exportUrl, { headers: { "Authorization": `Bearer ${token}`, "X-Tenant-Id": localStorage.getItem('tenant') } });
      if (!response.ok) throw new Error('Failed to export data');
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = 'services.csv';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error.message);
      setError('Failed to export data. Please try again.');
    } finally { setExporting(false); }
  };

  // ── Column definitions ─────────────────────────────────────────────────────

  const handleView = useCallback((item) => { setSelectedRecord(item); setModalOpen(true); }, []);
  const handleEditCb = useCallback((item) => handleEdit(item), []);

  const tableColumns = React.useMemo(() => [
    { id: "ticket_code",    label: "Ticket ID"       },
    { id: "customer_name",  label: "Customer Name"   },
    { id: "customer_phone", label: "Customer Phone"  },
    { id: "wa_name",        label: "WA Name"         },
    { id: "wa_number",      label: "WA Phone"        },
    {
      id: "is_existing_customer",
      label: "Existing Customer",
      render: (item) => (
        <Chip
          label={item.is_existing_customer ? 'Yes' : 'No'}
          color={item.is_existing_customer ? 'success' : 'info'}
          size="small"
        />
      ),
    },
    { id: "city",       label: "City",       render: (item) => item.address_details?.city },
    { id: "issue_type", label: "Issue Type"  },
    {
      id: "service_status",
      label: "Status",
      render: (item) => <Chip label={item.service_status} color={getStatusColor(item.service_status)} size="small" />,
    },
    { id: "updated_at", label: "Updated At", render: (item) => formatWithTzLabel(item.updated_at, "DD/MM/YYYY, hh:mm A") },
    { id: "updated_by", label: "Updated By"  },
    {
      id: "__actions__",
      label: "Actions",
      render: (item) => (
        <Box display="flex" gap={1}>
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleView(item); }}>
            <EyeIcon />
          </IconButton>
          <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); handleEditCb(item); }}>
            <EditIcon />
          </IconButton>
        </Box>
      ),
    },
  ], [handleView, handleEditCb]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const DetailRow = ({ label, value, fullWidth = false, preWrap = false }) => (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>{label}</Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" style={preWrap ? { whiteSpace: 'pre-line' } : undefined}>{value}</Typography>
      </Paper>
    </Grid>
  );

  return (
    <DoorLocksFeatureLayout
      activeTab={tab} onTabChange={handleTabChange} onBack={handleBack}
      counts={effectiveCounts} loadingStats={loadingStats}
    >
      {loadingStats ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Filter bar */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Box display="flex" alignItems="center" sx={{ width: "100%", minWidth: 0, gap: 1, flexWrap: "wrap" }}>
              <Box sx={{ flex: "1 1 280px", minWidth: 220 }}>
                <TableSearchInput
                  searchQuery={searchQuery}
                  onSearchChange={handleSearch}
                  tooltip="Search by Ticket ID, Customer Phone, Customer Name, City"
                  placeholder="Search by Ticket ID, Customer Phone, Customer Name, City"
                  charCount={charCount}
                />
              </Box>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={issueTypeFilter}
                onMenuSelect={(value) => { setIssueTypeFilter(value); setPage(0); }}
                menuItems={issueTypeOptions}
                sx={{ minWidth: 140, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {issueTypeOptions.find(o => o.value === issueTypeFilter)?.label ?? "All Issue Types"}
              </AppDropdown>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={lockTypeFilter}
                onMenuSelect={(value) => { setLockTypeFilter(value); setPage(0); }}
                menuItems={lockTypeOptions}
                sx={{ minWidth: 140, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {lockTypeOptions.find(o => o.value === lockTypeFilter)?.label ?? "All Lock Types"}
              </AppDropdown>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={cityFilter}
                onMenuSelect={(value) => { setCityFilter(value); setPage(0); }}
                menuItems={cityOptions}
                sx={{ minWidth: 130, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {cityOptions.find(o => o.value === cityFilter)?.label ?? "All Cities"}
              </AppDropdown>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={statusFilter}
                onMenuSelect={(value) => { setStatusFilter(value); setPage(0); }}
                menuItems={statusOptions}
                sx={{ minWidth: 130, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {statusOptions.find(o => o.value === statusFilter)?.label ?? "All Statuses"}
              </AppDropdown>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={updatedByFilter}
                onMenuSelect={(value) => { setUpdatedByFilter(value); setPage(0); }}
                menuItems={updatedByOptions}
                sx={{ minWidth: 150, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {updatedByOptions.find(o => o.value === updatedByFilter)?.label ?? "All Updated By"}
              </AppDropdown>

              <Box sx={{ width: 175, flexShrink: 0 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <AppDatePicker
                    label="Updated Date"
                    fullWidth
                    value={updatedDateFilter ? dayjs(updatedDateFilter) : null}
                    onChange={(newValue) => { setUpdatedDateFilter(newValue ? newValue.format('YYYY-MM-DD') : ''); setPage(0); }}
                  />
                </LocalizationProvider>
              </Box>

              <ButtonOne
                variant="contained"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                onClick={() => { setSearchQuery(''); setCityFilter(''); setIssueTypeFilter(''); setLockTypeFilter(''); setStatusFilter(''); setUpdatedByFilter(''); setUpdatedDateFilter(''); setCharCount(0); setPage(0); }}
              >
                Reset
              </ButtonOne>

              <ButtonTwo
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={loading || exporting}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {exporting ? 'Exporting...' : 'Export CSV'}
              </ButtonTwo>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          <AppTable
            columns={tableColumns}
            rows={servicesData}
            rowKey="service_id"
            loading={loading}
            emptyMessage="No services found"
            maxHeight={440}
            headCellSx={{ backgroundColor: "#f5f5f5" }}
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />

          {/* View Details Modal */}
          <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Service Details</DialogTitle>
            <DialogContent dividers>
              {selectedRecord && (
                <Grid container spacing={2}>
                  <DetailRow label="Ticket ID"        value={selectedRecord.ticket_code}            />
                  <DetailRow label="Lock Model Name"  value={selectedRecord.serial_number}          />
                  <DetailRow label="Customer Phone"   value={selectedRecord.customer_phone}         />
                  <DetailRow label="WA Phone"         value={selectedRecord.wa_number}              />
                  <DetailRow label="Customer Name"    value={selectedRecord.customer_name}          />
                  <DetailRow label="WA Name"          value={selectedRecord.wa_name}                />
                  <DetailRow label="Existing Customer" value={selectedRecord.is_existing_customer ? 'Yes' : 'No'} />
                  <DetailRow label="City"             value={selectedRecord.address_details?.city}  />
                  <DetailRow label="Status"           value={selectedRecord.service_status}         />
                  <DetailRow label="Issue Type"       value={selectedRecord.issue_type}             />
                  {selectedRecord.issue_description && (
                    <DetailRow label="Issue Description" value={selectedRecord.issue_description} fullWidth preWrap />
                  )}
                  {selectedRecord.address_details && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Address</Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="body2">
                          {selectedRecord.address_details.flat}, {selectedRecord.address_details.street}<br />
                          {selectedRecord.address_details.area}<br />
                          {selectedRecord.address_details.city}, {selectedRecord.address_details.state}<br />
                          PIN: {selectedRecord.address_details.pincode}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  {selectedRecord.requirements && (
                    <DetailRow label="Requirements" value={selectedRecord.requirements} fullWidth preWrap />
                  )}
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setModalOpen(false)} color="primary">Close</Button>
            </DialogActions>
          </Dialog>

          {/* Edit Modal */}
          <ServiceEditModal
            open={editModalOpen}
            onClose={() => { setEditModalOpen(false); setEditError(null); setEditSuccess(false); }}
            formData={editFormData}
            onInputChange={(field, value) => setEditFormData(prev => ({ ...prev, [field]: value }))}
            onAddressChange={(field, value) => setEditFormData(prev => ({ ...prev, address: { ...prev.address, [field]: value } }))}
            onUpdate={handleUpdate}
            error={editError}
            success={editSuccess}
          />
        </>
      )}
    </DoorLocksFeatureLayout>
  );
}